import { useContext, useMemo, useState } from 'react'
import { DesignContext } from 'contexts/DesignContext'
import objectPath from 'object-path'
import { useTimeFormat } from 'contexts/TimeFormatContext'
import { Form, Col, Row, Badge } from 'react-bootstrap'
import Label from './Label'
import Icon from './Icon'
import { getDatasourceValue, getDateTimeValue } from '../action/Data'
import { formatCurrency } from '../action/Title'

const CustomBadge = (props) => {
  let { uischema, errors = {}, config, isModalTitle } = props
  let {
    key,
    elementWidth,
    controlId,
    require,
    prependText,
    appendText,
    prependIcon = null,
    appendIcon = null,
    badgeColorField,
    textColorField,
    badgeTitle,
    badgeJoin = '',
    badgeType = 'default',
  } = uischema

  const {
    isDynamic = false,
    dynamicName = '',
    dynamicIndex = 0,
    dynamicData = {},
    onUpdateFormData = () => {},
    isArray,
    arrayName,
  } = config

  const designContext = useContext(DesignContext)
  const datasourceValue = objectPath.get(designContext, `@datasource`) || {}
  const { formatDate, formatTime, formatDateTime } = useTimeFormat()
  const {
    searchData = {},
    jsonForm,
    modalForm,
    openModal,
    designerProps,
    elementAction = {},
    isUiChanged,
  } = designContext
  const uiChanged = objectPath(isUiChanged)
  const viewListItem = uiChanged.get(['viewListItem', uischema.key])

  const [label, setLabel] = useState(null)

  const uiKey = useMemo(() => {
    if (isDynamic) {
      if (designerProps) {
        let elementName = designerProps.filter(
          ({ propType }) => propType === 'name',
        )
        let uiName = elementName.find(
          ({ prop }) => `@${prop}` === uischema.name,
        )
        if (uiName) {
          setLabel(uiName.systemValue)
          return uiName.systemName
        }
      }
      return uischema.name
    }
    return uischema.key
  }, [isDynamic])

  const fieldName = isArray
    ? `@${arrayName}.${uischema.name}`
    : isDynamic
    ? `${dynamicName}.${dynamicIndex}.${uischema.name}`
    : uischema.name
  const fieldKey = isArray
    ? `@${arrayName}.${uischema.key}`
    : isDynamic
    ? `${dynamicName}.${dynamicIndex}.${uischema.key}`
    : uischema.key

  const fieldBadgeColorField = isArray
    ? `@${arrayName}.${badgeColorField}`
    : isDynamic
    ? `${dynamicName}.${dynamicIndex}.${badgeColorField}`
    : badgeColorField
  const fieldTextColorField = isArray
    ? `@${arrayName}.${textColorField}`
    : isDynamic
    ? `${dynamicName}.${dynamicIndex}.${textColorField}`
    : textColorField

  const formData = jsonForm.formData
  const badgeColor = fieldBadgeColorField
    ? objectPath.get(formData, fieldBadgeColorField) ?? uischema.badgeColor
    : uischema.badgeColor
  const textColor = fieldTextColorField
    ? objectPath.get(formData, fieldTextColorField) ?? uischema.textColor
    : uischema.textColor

  let currentSearch = useMemo(() => {
    let fieldSearch = objectPath.get(jsonForm, 'search.field') || []
    let found = fieldSearch.find(({ key }) => key === uiKey)
    return found ? true : false
  }, [uiKey])

  const data = isDynamic ? dynamicData : currentSearch ? searchData : formData

  let value = useMemo(() => {
    if (badgeTitle) {
      if (badgeType === 'value' && badgeTitle) {
        let valueList = []
        badgeTitle.forEach((item) => {
          let { value, title, type } = item
          if (type === 'badge') return
          if (title) {
            let valueItem = []
            title.forEach((subItem) => {
              let key = subItem.value
              let isNew = objectPath.get(subItem, '__isNew__')
              let { isElement, isFormComponent, formComponentName, optionKey } =
                subItem
              let badgeTitle = ''
              if (isNew) {
                if (key === '@numberOrder') {
                  badgeTitle = dynamicIndex + 1
                } else if (key === '@seq') {
                  badgeTitle = objectPath.get(data, '@seq')
                } else if (key.startsWith('@')) {
                  let itemKey = key.replace('@', '')
                  if (viewListItem) {
                    let data = viewListItem.item
                    badgeTitle = objectPath.get(data, itemKey)
                  } else if (isModalTitle) {
                    badgeTitle = objectPath.get(
                      datasourceValue,
                      `receiveProps.${itemKey}`,
                    )
                  } else {
                    badgeTitle = objectPath.get(dynamicData, itemKey)
                  }
                } else {
                  badgeTitle = subItem.value
                }
              } else if (isElement) {
                badgeTitle = objectPath.get(data, key)
              } else if (isFormComponent) {
                badgeTitle = objectPath.get(data, `${formComponentName}.${key}`)
              } else {
                badgeTitle = getDatasourceValue({
                  item,
                  key,
                  datasourceValue,
                  formatDate,
                  formatTime,
                  formatDateTime,
                })
              }
              if (type === 'currency') {
                if (key.startsWith('@')) {
                  badgeTitle = formatCurrency(badgeTitle)
                }
              } else if (type === 'dateTime') {
                if (key.startsWith('@')) {
                  badgeTitle = getDateTimeValue(badgeTitle, item, {
                    formatDate,
                    formatTime,
                    formatDateTime,
                  })
                }
              }
              valueItem.push(badgeTitle)
            })
            valueList.push(valueItem.join(' '))
          } else {
            let badgeTitle = objectPath.get(datasourceValue, value)
            if (badgeTitle) {
              valueList.push(badgeTitle)
            } else {
              valueList.push(value)
            }
          }
        })
        return valueList.join(badgeJoin)
      }
      return ''
    }
    let value = objectPath.get(data, uiKey)
    return value?.toString()
  }, [JSON.stringify(data)])

  let errorArray = objectPath.get(errors, key) || []

  let hasRequire = objectPath.has(elementAction, ['isRequire', key])
  let requireMessage = objectPath.get(elementAction, ['isRequire', key])
  let showRequireMessage = hasRequire
    ? typeof requireMessage !== 'boolean'
    : require
  let isRequire = hasRequire || require
  let showRequire = value ? false : isRequire ? true : false

  let hasWarning = objectPath.has(elementAction, ['isWarning', key])
  let warningMessage = objectPath.get(elementAction, ['isWarning', key])
  let showWarningMessage = hasWarning
    ? typeof warningMessage !== 'boolean'
    : warningMessage

  if (!value) return ''

  return (
    <Form.Group className="mb-0" controlId={controlId}>
      <Row className="w-100 mx-0">
        <Label
          {...props}
          uischema={uischema}
          label={label}
          isRequire={isRequire}
        />
        <Col sm={elementWidth} className="p-0">
          <Badge
            id={fieldKey}
            name={fieldName}
            style={{
              backgroundColor: badgeColor,
              color: textColor,
              display: 'inline-flex',
              justifyContent: 'center',
              width: 'auto',
              fontSize: uischema.fontSize,
            }}
          >
            <Icon iconName={prependIcon} />
            <p
              className="px-1 mb-0"
              style={{
                color: textColor,
                fontSize: uischema.fontSize,
                fontWeight: `${uischema.fontWeight} !important`,
              }}
            >
              {prependText} {value} {appendText}
            </p>
            <Icon iconName={appendIcon} />
          </Badge>
        </Col>
      </Row>
      <ul className="m-0 mt-2">
        {showRequire && showRequireMessage && requireMessage && (
          <li className="text-danger">{requireMessage}</li>
        )}
        {showWarningMessage && warningMessage && (
          <li className="text-danger">{warningMessage}</li>
        )}
        {errorArray.map((error, index) => (
          <li key={`error-${key}-${index + 1}`} className="text-danger">
            {error}
          </li>
        ))}
      </ul>
    </Form.Group>
  )
}

export default CustomBadge
